import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Check, Star, ExternalLink, CalendarIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const CALENDLY_URL = 'https://calendly.com/roch-kitchen/roch-consultation';

export default function Consultation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const consultationType = location.state?.type || 'quick_review';
  const reportId = location.state?.reportId || null;
  const typeName = consultationType === 'quick_review' ? 'Quick Review' : 'Pre-Investment Strategy Session';

  const handleSaveAndBook = async () => {
    setLoading(true);
    try {
      await api.createConsultation({
        consultation_type: consultationType,
        report_id: reportId,
        notes: notes,
      });
      setSaved(true);
      // Open Calendly in new tab
      window.open(CALENDLY_URL, '_blank');
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button onClick={() => navigate('/')}><img src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/z3ko7m68_Cake_Studio.png" alt="ROCH" className="h-14 w-auto" /></button>
          <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="text-xs text-[#1D1D1F]/60 rounded-full">Dashboard</Button>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-6 md:px-12 py-12 md:py-20">

        {/* Success state — after saving */}
        {saved ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-emerald-600" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1D1D1F] tracking-tight mb-3" data-testid="consultation-confirmed">
              You're all set.
            </h2>
            <p className="text-sm text-[#1D1D1F]/60 font-medium mb-8 max-w-sm mx-auto">
              Your {typeName.toLowerCase()} request has been saved. Now pick a time that works for you on Calendly.
            </p>

            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calendly-link-btn"
              className="inline-flex items-center gap-2 bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-8 py-4 text-base font-semibold transition-all duration-500 active:scale-95 mb-4"
            >
              <CalendarIcon className="h-5 w-5" strokeWidth={1.5} />
              Book Your Slot on Calendly
              <ExternalLink className="h-4 w-4 opacity-50" strokeWidth={1.5} />
            </a>

            <p className="text-xs text-[#1D1D1F]/30 mb-8">Opens Calendly in a new tab</p>

            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-sm text-[#1D1D1F]/50 rounded-full"
            >
              Go to Dashboard
            </Button>
          </div>
        ) : (
          /* Booking form */
          <>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#F5F5F7] rounded-full px-4 py-1.5 mb-4">
                <CalendarIcon className="h-3.5 w-3.5 text-[#1D1D1F]/50" strokeWidth={1.5} />
                <span className="text-xs font-semibold text-[#1D1D1F]/70">{typeName}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1D1D1F] tracking-tight mb-3">
                Book Your Consultation
              </h1>
              <p className="text-sm text-[#1D1D1F]/50 font-medium max-w-sm mx-auto">
                Add any context below, then you'll pick a time slot on Calendly.
              </p>
            </div>

            <div className="bg-white rounded-3xl border border-[#E5E5EA] p-6 md:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] mb-6">
              <div className="space-y-5">
                {/* What to expect */}
                <div className="bg-[#F5F5F7] rounded-2xl p-4">
                  <h4 className="text-xs font-bold text-[#1D1D1F] mb-2">What to expect</h4>
                  {consultationType === 'quick_review' ? (
                    <ul className="space-y-1.5 text-xs text-[#1D1D1F]/60 font-medium">
                      <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} /> 20-30 minute video call</li>
                      <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} /> Review of your feasibility report</li>
                      <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} /> Key risks identified and direction correction</li>
                    </ul>
                  ) : (
                    <ul className="space-y-1.5 text-xs text-[#1D1D1F]/60 font-medium">
                      <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} /> 60-90 minute deep dive session</li>
                      <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} /> Full concept + financial review</li>
                      <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} /> Business model refinement + clear next steps</li>
                      <li className="flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={2} /> Post-call summary of recommendations</li>
                    </ul>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#1D1D1F]">Notes for the consultant (optional)</label>
                  <textarea
                    data-testid="consultation-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any specific questions, concerns, or areas you'd like to focus on..."
                    className="w-full bg-[#F5F5F7] border border-[#E5E5EA] rounded-2xl p-4 text-xs text-[#1D1D1F] resize-none h-24 focus:outline-none focus:ring-1 focus:ring-[#0071E3]"
                  />
                </div>

                <Button
                  data-testid="submit-consultation-btn"
                  onClick={handleSaveAndBook}
                  disabled={loading}
                  className="w-full bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full py-4 text-sm font-semibold transition-all duration-500 active:scale-95"
                >
                  {loading ? 'Saving...' : 'Continue to Book on Calendly'}
                  <ExternalLink className="ml-2 h-4 w-4 opacity-50" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            {/* Credibility */}
            <div className="flex items-center justify-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_unit-economics-3/artifacts/dfc8hr56_MCI%202023%20-%20Ting%20011136%20copy%202.jpg"
                alt="Rohit Chidurala"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="text-left">
                <div className="flex gap-0.5 mb-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-2.5 w-2.5 fill-[#1D1D1F] text-[#1D1D1F]" />
                  ))}
                </div>
                <p className="text-[10px] text-[#1D1D1F]/40 font-medium">
                  Rohit Chidurala — MasterChef India Telugu semifinalist
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
